import { EarningsInsight } from '@api/api/earnings-insights';
import { Icon } from '@design/components/icon/Icon';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import clsx from 'clsx';
import React from 'react';

import { EarningsInsightTooltip } from '../earnings-insight-tooltip/EarningsInsightTooltip';
import styles from './EarningsInsightIcon.module.scss';
import { getEarningsInsightIconName } from './utils';

export interface EarningsInsightIconProps {
  size?: number;
  width?: number;
  height?: number;
  entityTag: string;
  earningsInsight: EarningsInsight;
  isDisabled?: boolean;
  enableHover?: boolean;
  isBordered?: boolean;
  source: PosthogTrackingSource;
  customSize?: { width: number; height: number };
  isClickable?: boolean;
}

export const EarningsInsightIcon = ({
  entityTag,
  earningsInsight,
  isDisabled = false,
  enableHover = true,
  isBordered = true,
  customSize,
  size,
  source,
  isClickable = true,
  ...rest
}: EarningsInsightIconProps) => {
  const { openDrawer } = useEarningsInsightDrawer();
  const { trackInsightIconClicked } = usePosthogEvents();

  const isPreview = earningsInsight.type === 'preview';
  const iconProps = {
    iconName: getEarningsInsightIconName(isPreview, isBordered),
    viewBox: isBordered ? '0 0 30 32' : '0 0 12 12',
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isClickable) {
      return;
    }

    trackInsightIconClicked({
      source,
      type: isPreview ? 'earnings_preview' : 'earnings_recap',
      insightId: earningsInsight.earnings_id,
      entities: [entityTag],
    });
    openDrawer({
      entityTag,
      earningsId: earningsInsight.earnings_id,
      subtab: isPreview ? SubTabsType.Preview : SubTabsType.Recap,
      source,
    });
  };

  return (
    <TooltipContainer
      label={
        <div onClick={handleClick} role="presentation">
          <EarningsInsightTooltip
            earningsInsight={earningsInsight}
            isPreview={isPreview}
            iconProps={iconProps}
          />
        </div>
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!enableHover || isDisabled}
      inPortal
      withSafePolygon
    >
      <Icon
        {...iconProps}
        {...rest}
        {...(customSize
          ? { width: customSize.width, height: customSize.height }
          : { size })}
        className={clsx({
          [styles.iconHoverable]: enableHover && isClickable,
          [styles.iconDisabledUnbordered]: isDisabled && !isBordered,
          [styles.iconDisabledBordered]: isDisabled && isBordered,
        })}
        onClick={handleClick}
      />
    </TooltipContainer>
  );
};
